import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useUser } from "@gadgetinc/react";
import { api } from "../api";
import { hasAnyRole } from "../lib/roleUtils";
import { applyProtectedFieldPolicyToFields } from "../../src/liveQa/protectedFieldPolicy.js";

const SOURCE_TYPE_OPTIONS = [
  { value: "pcr_sample", label: "Completed PCR sample" },
  { value: "blank_form", label: "Blank/exported chart form" },
];

const REVIEW_DOMAIN_OPTIONS = [
  { value: "clinical", label: "Clinical" },
  { value: "operational", label: "Operational" },
  { value: "billing", label: "Billing" },
];

const FACILITY_TYPES = [
  "hospital_system",
  "fire_department",
  "ems_service",
  "private_ambulance",
  "urgent_care",
  "clinic",
  "long_term_care",
  "rehabilitation_center",
  "educational_institution",
  "government_agency",
  "other",
];

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

async function parseJsonResponse(response, fallbackMessage) {
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    throw new Error(text ? `${fallbackMessage}: ${text.slice(0, 160)}` : fallbackMessage);
  }
  return await response.json();
}

function getRouteErrorMessage(payload, fallbackMessage) {
  const base = payload?.error || fallbackMessage;
  return payload?.debugId ? `${base} (Debug ID: ${payload.debugId})` : base;
}

async function fileToBase64Payload(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
  const commaIndex = dataUrl.indexOf(",");
  const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  if (!base64) {
    throw new Error("Failed to encode file for upload");
  }
  return {
    base64,
    byteSize: file.size,
    mimeType: file.type || "application/pdf",
    fileName: file.name || "template-candidate.pdf",
  };
}

function defaultAgencyDraft() {
  return {
    name: "",
    city: "",
    state: "",
    facilityType: "ems_service",
  };
}

function defaultCreateDraft(institutions = [], releases = []) {
  return {
    name: "",
    description: "",
    ownerInstitution: institutions[0] || "",
    ownerAgencyId: "",
    sourceType: "pcr_sample",
    nemsisVersion: releases.find((entry) => entry.isDefault)?.version || releases[0]?.version || "3.5.1",
    redactionMode: "auto",
    file: null,
  };
}

function mergeCreateDraftDefaults(current, institutions = [], releases = []) {
  const defaults = defaultCreateDraft(institutions, releases);
  return {
    ...defaults,
    ...current,
    ownerInstitution: current?.ownerInstitution || defaults.ownerInstitution,
    nemsisVersion: current?.nemsisVersion || defaults.nemsisVersion,
    sourceType: current?.sourceType || defaults.sourceType,
  };
}

function statusColor(status) {
  switch (status) {
    case "active":
      return "green";
    case "review_ready":
      return "blue";
    case "analyzing":
    case "queued":
      return "orange";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

function normalizeSectionKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}

function phiHintLabel(hintType) {
  switch (String(hintType || "")) {
    case "template_redacted":
      return "Template redaction";
    case "patient_name":
      return "Patient name";
    case "date_of_birth":
      return "DOB";
    case "address":
      return "Address";
    case "contact":
      return "Contact info";
    case "record_identifier":
      return "Record ID";
    case "patient_demographics":
      return "Patient demographic";
    case "provider_name":
      return "Provider name";
    case "signature":
      return "Signature";
    default:
      return "Sensitive field";
  }
}

function hasSystemAdminRole(roleSource) {
  const entries = Array.isArray(roleSource) ? roleSource : roleSource ? [roleSource] : [];
  return entries.some((entry) => {
    const raw = typeof entry === "string"
      ? entry
      : entry?.name || entry?.key || entry?.role || "";
    const normalized = String(raw || "").trim().toLowerCase();
    return ["system-admin", "system_admin", "sysadmin"].includes(normalized);
  });
}

function normalizeTemplateFromApi(template) {
  if (!template) return template;
  return {
    ...template,
    fields: applyProtectedFieldPolicyToFields(template.fields || []),
  };
}

export default function LiveQaTemplateBuilderRoute() {
  const user = useUser();
  const toast = useToast();
  const agencyModal = useDisclosure();
  const fileInputRef = useRef(null);
  const selectedTemplateFileInputRef = useRef(null);
  const templateRequestSequenceRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [building, setBuilding] = useState(false);
  const [config, setConfig] = useState({
    institutions: [],
    agencies: [],
    nemsisReleases: [],
    templates: [],
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [selectedTemplateRedactionMode, setSelectedTemplateRedactionMode] = useState("auto");
  const [createDraft, setCreateDraft] = useState(defaultCreateDraft());
  const [newAgency, setNewAgency] = useState(defaultAgencyDraft());
  const [nemsisSearch, setNemsisSearch] = useState({});
  const [nemsisResults, setNemsisResults] = useState({});
  const [error, setError] = useState("");

  const canAccess = hasAnyRole(user?.roles, ["admin"]);
  const canOverrideProtectedFields = hasSystemAdminRole(user?.roles || user?.roleList || []);
  const canManageProtectionRequests = canAccess && !canOverrideProtectedFields;

  const selectedTemplateStatus = selectedTemplate?.builder?.status || "draft";
  const pageBg = "rgba(15, 23, 42, 0.72)";
  const cardBg = "rgba(15, 23, 42, 0.78)";
  const borderColor = "whiteAlpha.200";
  const textPrimary = "white";
  const textSecondary = "gray.300";
  const inputBg = "whiteAlpha.120";
  const pendingTemplateFile = selectedTemplateFile || selectedTemplateFileInputRef.current?.files?.[0] || null;
  const selectedTemplateSourceDocument = selectedTemplate?.builder?.sourceDocument || selectedTemplate?.sourceDocument || null;

  useEffect(() => {
    if (!canAccess) return;
    void loadInitial();
  }, [canAccess]);

  useEffect(() => {
    if (!selectedTemplateId || !["queued", "analyzing"].includes(selectedTemplateStatus)) return;
    const timer = window.setInterval(() => {
      void loadTemplate(selectedTemplateId, false);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [selectedTemplateId, selectedTemplateStatus]);

  const filteredAgencies = useMemo(() => {
    if (!createDraft.ownerInstitution) return config.agencies || [];
    return (config.agencies || []).filter((agency) => {
      const affiliations = Array.isArray(agency.institutionalAffiliations) ? agency.institutionalAffiliations : [];
      return agency.originalInstitution === createDraft.ownerInstitution || affiliations.includes(createDraft.ownerInstitution);
    });
  }, [config.agencies, createDraft.ownerInstitution]);

  const groupedFields = useMemo(() => {
    const protectedIds = new Set(
      Array.isArray(selectedTemplate?.fields)
        ? selectedTemplate.fields.filter((field) => field?.isProtected).map((field) => String(field?.id || ""))
        : []
    );
    const sectionOrder = Array.isArray(selectedTemplate?.builder?.sections)
      ? selectedTemplate.builder.sections
      : [];
    const sectionOrderIndex = new Map(
      sectionOrder.map((label, index) => [String(label || "").trim(), index])
    );
    const groups = new Map();
    for (const field of selectedTemplate?.fields || []) {
      const sectionLabel = String(field?.section || "").trim() || "Other";
      if (!groups.has(sectionLabel)) {
        groups.set(sectionLabel, []);
      }
      groups.get(sectionLabel).push(field);
    }
    const entries = Array.from(groups.entries()).map(([label, fields]) => ({
      key: normalizeSectionKey(label),
      label,
      fields,
      redactedCount: fields.filter((field) => protectedIds.has(String(field?.id || ""))).length,
    }));
    entries.sort((left, right) => {
      const leftIndex = sectionOrderIndex.has(left.label) ? sectionOrderIndex.get(left.label) : Number.MAX_SAFE_INTEGER;
      const rightIndex = sectionOrderIndex.has(right.label) ? sectionOrderIndex.get(right.label) : Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) return leftIndex - rightIndex;
      return 0;
    });
    if (entries.length === 1 && entries[0].label === "Other") {
      return [
        {
          key: "ungrouped",
          label: "",
          fields: entries[0].fields,
          redactedCount: entries[0].redactedCount,
        },
      ];
    }
    return entries.map((entry) => ({
      ...entry,
      hasSensitiveFields: entry.redactedCount > 0,
      isLocked: entry.redactedCount > 0 && entry.redactedCount === entry.fields.length,
    }));
  }, [selectedTemplate]);

  const extractionBreakdown = useMemo(() => {
    const fields = Array.isArray(selectedTemplate?.fields) ? selectedTemplate.fields : [];
    let vision = 0;
    let deterministic = 0;
    let other = 0;
    for (const field of fields) {
      const source = String(field?.evidence?.source || "").trim().toLowerCase();
      if (source === "vision") {
        vision += 1;
      } else if (source === "deterministic") {
        deterministic += 1;
      } else {
        other += 1;
      }
    }
    return {
      total: fields.length,
      vision,
      deterministic,
      other,
      method: selectedTemplate?.builder?.extractionSummary?.extractionMethod || "",
    };
  }, [selectedTemplate]);

  const isFallbackOnlyExtraction = useMemo(() => {
    return extractionBreakdown.total > 0 && extractionBreakdown.vision === 0 && extractionBreakdown.deterministic === extractionBreakdown.total;
  }, [extractionBreakdown]);

  const redactedFieldIds = useMemo(() => {
    return new Set(
      Array.isArray(selectedTemplate?.builder?.redactionHints)
        ? selectedTemplate.builder.redactionHints.map((hint) => String(hint?.fieldId || "")).filter(Boolean)
        : []
    );
  }, [selectedTemplate]);

  const redactionHintsByFieldId = useMemo(() => {
    const next = new Map();
    for (const hint of Array.isArray(selectedTemplate?.builder?.redactionHints) ? selectedTemplate.builder.redactionHints : []) {
      if (!hint?.fieldId) continue;
      next.set(String(hint.fieldId), hint);
    }
    return next;
  }, [selectedTemplate]);

  const protectedFieldIds = useMemo(() => {
    return new Set(
      Array.isArray(selectedTemplate?.fields)
        ? selectedTemplate.fields.filter((field) => field?.isProtected).map((field) => String(field.id || ""))
        : []
    );
  }, [selectedTemplate]);

  const defaultExpandedSections = useMemo(() => {
    const unlockedIndex = groupedFields.findIndex((section) => !section.isLocked);
    if (unlockedIndex >= 0) return [unlockedIndex];
    return groupedFields.length ? [0] : [];
  }, [groupedFields]);

  async function loadInitial(selectTemplateId = "") {
    setLoading(true);
    setError("");
    try {
      const [configResponse, templatesResponse] = await Promise.all([
        api.fetch("/live-qa/template-builder-config"),
        api.fetch("/live-qa/templates"),
      ]);
      const configJson = await parseJsonResponse(configResponse, "Template builder config request did not return JSON");
      const templatesJson = await parseJsonResponse(templatesResponse, "Templates request did not return JSON");
      if (!configJson.success) throw new Error(getRouteErrorMessage(configJson, "Failed to load builder config"));
      if (!templatesJson.success) throw new Error(getRouteErrorMessage(templatesJson, "Failed to load templates"));
      const normalizedTemplates = (templatesJson.templates || []).map((template) => normalizeTemplateFromApi(template));
      setConfig(configJson);
      setTemplates(normalizedTemplates);
      setCreateDraft((current) => mergeCreateDraftDefaults(current, configJson.institutions, configJson.nemsisReleases));
      const targetId = selectTemplateId || selectedTemplateId || normalizedTemplates?.[0]?.id || "";
      if (targetId) {
        await loadTemplate(targetId, false);
      } else {
        setSelectedTemplateId("");
        setSelectedTemplate(null);
      }
    } catch (loadError) {
      setError(loadError.message || "Failed to load template builder");
    } finally {
      setLoading(false);
    }
  }

  async function loadTemplate(templateId, setBusy = true, options = {}) {
    if (!templateId) return;
    const requestSequence = ++templateRequestSequenceRef.current;
    const preservePendingFile = options.preservePendingFile ?? (templateId === selectedTemplateId);
    if (setBusy) setLoading(true);
    try {
      const response = await api.fetch(`/live-qa/templates/${templateId}`);
      const json = await parseJsonResponse(response, "Template detail request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to load template"));
      if (requestSequence !== templateRequestSequenceRef.current) return;
      const normalizedTemplate = normalizeTemplateFromApi(json.template);
      setSelectedTemplateId(templateId);
      setSelectedTemplate(normalizedTemplate);
      if (!preservePendingFile || normalizedTemplate.id !== templateId) {
        setSelectedTemplateFile(null);
        setSelectedTemplateRedactionMode("auto");
        if (selectedTemplateFileInputRef.current) {
          selectedTemplateFileInputRef.current.value = "";
        }
      }
      setTemplates((current) => {
        const withoutSelected = current.filter((entry) => entry.id !== normalizedTemplate.id);
        return [normalizedTemplate, ...withoutSelected];
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load template");
    } finally {
      if (setBusy) setLoading(false);
    }
  }

  function patchSelectedTemplate(updater) {
    setSelectedTemplate((current) => {
      if (!current) return current;
      const next = typeof updater === "function" ? updater(clone(current)) : updater;
      return next;
    });
  }

  function updateField(fieldId, updater) {
    patchSelectedTemplate((current) => ({
      ...current,
      fields: (current.fields || []).map((field) => (field.id === fieldId ? updater({ ...field }) : field)),
    }));
  }

  function addManualField() {
    patchSelectedTemplate((current) => ({
      ...current,
      fields: [
        ...(current.fields || []),
        {
          id: `manual_${Date.now()}`,
          sourceLabel: "",
          plainEnglishLabel: "",
          reviewDomain: "clinical",
          dataType: "string",
          confidence: 1,
          mandatory: false,
          missingSeverity: "warn",
          hasCustomRule: false,
          customRuleText: "",
          generatedInstructionText: "",
          generatedInstructionData: null,
          instructionStatus: "idle",
          hasNemsisAlignment: false,
          nemsisElementId: "",
          nemsisCandidates: [],
          aiSuggestions: {
            mandatoryCandidate: false,
            customRuleCandidate: false,
            customRuleHint: "",
            plainEnglishLabel: "",
          },
          evidence: {
            source: "manual",
            locationDescription: "",
            extractedValueSample: null,
          },
        },
      ],
    }));
  }

  async function handleCreateTemplate() {
    const resolvedOwnerInstitution = createDraft.ownerInstitution || user?.institution || config.institutions?.[0] || "";
    const selectedFile = createDraft.file || fileInputRef.current?.files?.[0] || null;
    const missing = [];
    if (!resolvedOwnerInstitution) missing.push("owner institution");
    if (!selectedFile) missing.push("source document");
    if (missing.length) {
      setError(`Missing required field${missing.length > 1 ? "s" : ""}: ${missing.join(" and ")}`);
      return;
    }
    setCreating(true);
    setError("");
    try {
      const sourceDocument = await fileToBase64Payload(selectedFile);
      const response = await api.fetch("/live-qa/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: createDraft.name || `${resolvedOwnerInstitution} Template Candidate`,
          description: createDraft.description,
          ownerInstitution: resolvedOwnerInstitution,
          ownerAgencyId: createDraft.ownerAgencyId || null,
          sourceType: createDraft.sourceType,
          nemsisVersion: createDraft.nemsisVersion,
          redactionMode: createDraft.redactionMode,
          sourceDocument,
          autoBuild: true,
        }),
      });
      const json = await parseJsonResponse(response, "Create template request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to create template"));
      toast({ status: "success", title: "Template draft created", description: "Visual extraction is running in the background." });
      await loadInitial(json.template.id);
    } catch (createError) {
      setError(createError.message || "Failed to create template");
    } finally {
      setCreating(false);
    }
  }

  async function saveTemplate({ autoBuild = false } = {}) {
    if (!selectedTemplate) return;
    setError("");
    let sourceDocument = null;
    const effectiveTemplateFile = selectedTemplateFile || selectedTemplateFileInputRef.current?.files?.[0] || null;
    if (effectiveTemplateFile) {
      sourceDocument = await fileToBase64Payload(effectiveTemplateFile);
    }
    const response = await api.fetch("/live-qa/templates", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        templateId: selectedTemplate.id,
        name: selectedTemplate.name,
        description: selectedTemplate.description,
        ownerInstitution: selectedTemplate.builder?.ownerInstitution || selectedTemplate.institution,
        ownerAgencyId: selectedTemplate.builder?.ownerAgency?.id || null,
        sourceType: selectedTemplate.builder?.sourceType || "pcr_sample",
        nemsisVersion: selectedTemplate.builder?.nemsisVersion || "3.5.1",
        redactionMode: selectedTemplateRedactionMode,
        documentFamilyKey: selectedTemplate.builder?.documentFamilyKey || "",
        documentFamilyLabel: selectedTemplate.builder?.documentFamilyLabel || "",
        assignments: selectedTemplate.builder?.assignments || [],
        fields: selectedTemplate.fields || [],
        status: selectedTemplate.builder?.status || "draft",
        isActive: selectedTemplate.isActive,
        isDefault: selectedTemplate.isDefault,
        autoBuild,
        sourceDocument,
      }),
    });
    const json = await parseJsonResponse(response, "Save template request did not return JSON");
    if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to save template"));
    setSelectedTemplateFile(null);
    setSelectedTemplateRedactionMode("auto");
    if (selectedTemplateFileInputRef.current) {
      selectedTemplateFileInputRef.current.value = "";
    }
    if (json.replacedTemplateId) {
      setTemplates((current) => current.filter((entry) => entry.id !== json.replacedTemplateId));
    }
    setSelectedTemplate(normalizeTemplateFromApi(json.template));
    await loadInitial(json.template.id);
    return json.template;
  }

  async function handleSaveTemplate() {
    if (!selectedTemplate) return;
    setSaving(true);
    setError("");
    try {
      const template = normalizeTemplateFromApi(await saveTemplate({ autoBuild: false }));
      toast({
        status: "success",
        title: "Template saved",
        description: selectedTemplateFile ? "Source document updated." : undefined,
      });
      setSelectedTemplate(template);
    } catch (saveError) {
      setError(saveError.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  }

  async function handleQueueBuild() {
    if (!selectedTemplate) return;
    setBuilding(true);
    setError("");
    try {
      if (pendingTemplateFile) {
        const template = await saveTemplate({ autoBuild: true });
        patchSelectedTemplate((current) => ({
          ...current,
          ...template,
          builder: {
            ...(template.builder || current.builder || {}),
            status: "queued",
            buildProgressCurrent: "Queued for analysis",
            buildProgressPercent: 0,
            buildProgressSteps: [
              { label: "Queued", state: "active" },
              { label: "Starting analysis", state: "pending" },
              { label: "Downloading source document", state: "pending" },
              { label: "Extracting fields", state: "pending" },
              { label: "Compiling template", state: "pending" },
            ],
          },
        }));
        toast({ status: "info", title: "File attached and build queued" });
        return;
      }
      if (!selectedTemplateSourceDocument?.url) {
        throw new Error("Attach a source document before rebuilding this template");
      }
      const response = await api.fetch("/live-qa/template-build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: selectedTemplate.id }),
      });
      const json = await parseJsonResponse(response, "Build template request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to queue build"));
      patchSelectedTemplate((current) => ({
        ...current,
        builder: {
          ...(current.builder || {}),
          status: "queued",
          buildProgressCurrent: "Queued for analysis",
          buildProgressPercent: 0,
          buildProgressSteps: [
            { label: "Queued", state: "active" },
            { label: "Starting analysis", state: "pending" },
            { label: "Downloading source document", state: "pending" },
            { label: "Extracting fields", state: "pending" },
            { label: "Compiling template", state: "pending" },
          ],
        },
      }));
      toast({ status: "info", title: "Build queued" });
    } catch (buildError) {
      setError(buildError.message || "Failed to queue build");
    } finally {
      setBuilding(false);
    }
  }

  async function handleGenerateInstruction(field) {
    if (!selectedTemplate || !field?.id) return;
    updateField(field.id, (entry) => ({ ...entry, instructionStatus: "running" }));
    try {
      const response = await api.fetch("/live-qa/template-field-instruction", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          fieldId: field.id,
        }),
      });
      const json = await parseJsonResponse(response, "Field instruction request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to queue field instruction"));
      toast({ status: "info", title: "Instruction generation queued" });
      await loadTemplate(selectedTemplate.id, false);
    } catch (instructionError) {
      updateField(field.id, (entry) => ({ ...entry, instructionStatus: "failed" }));
      setError(instructionError.message || "Failed to queue field instruction");
    }
  }

  async function handleActivateTemplate() {
    if (!selectedTemplate) return;
    setSaving(true);
    setError("");
    try {
      await handleSaveTemplate();
      const response = await api.fetch("/live-qa/template-activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          assignments: selectedTemplate.builder?.assignments || [],
        }),
      });
      const json = await parseJsonResponse(response, "Activate template request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to activate template"));
      setSelectedTemplate(normalizeTemplateFromApi(json.template));
      toast({ status: "success", title: "Template activated" });
      await loadInitial(json.template.id);
    } catch (activateError) {
      setError(activateError.message || "Failed to activate template");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTemplate() {
    if (!selectedTemplate) return;
    const confirmed = window.confirm(`Delete template "${selectedTemplate.name}"?`);
    if (!confirmed) return;

    setSaving(true);
    setError("");
    try {
      const response = await api.fetch(`/live-qa/templates/${selectedTemplate.id}`, {
        method: "DELETE",
      });
      const json = await parseJsonResponse(response, "Delete template request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to delete template"));
      toast({ status: "success", title: "Template deleted" });
      setSelectedTemplate(null);
      setSelectedTemplateId("");
      setSelectedTemplateFile(null);
      setSelectedTemplateRedactionMode("auto");
      if (selectedTemplateFileInputRef.current) {
        selectedTemplateFileInputRef.current.value = "";
      }
      await loadInitial();
    } catch (deleteError) {
      setError(deleteError.message || "Failed to delete template");
    } finally {
      setSaving(false);
    }
  }

  async function handleNemsisSearch(fieldId, query) {
    setNemsisSearch((current) => ({ ...current, [fieldId]: query }));
    if (!query || query.trim().length < 2) {
      setNemsisResults((current) => ({ ...current, [fieldId]: [] }));
      return;
    }
    try {
      const response = await api.fetch(`/live-qa/template-nemsis?q=${encodeURIComponent(query)}&version=${encodeURIComponent(selectedTemplate?.builder?.nemsisVersion || "3.5.1")}`);
      const json = await parseJsonResponse(response, "NEMSIS search request did not return JSON");
      if (!json.success) throw new Error(getRouteErrorMessage(json, "NEMSIS search failed"));
      setNemsisResults((current) => ({ ...current, [fieldId]: json.results || [] }));
    } catch (searchError) {
      setError(searchError.message || "NEMSIS search failed");
    }
  }

  function addAssignment() {
    patchSelectedTemplate((current) => ({
      ...current,
      builder: {
        ...(current.builder || {}),
        assignments: [
          ...((current.builder?.assignments || []).length
            ? current.builder.assignments
            : [{ institution: current.builder?.ownerInstitution || current.institution, agencyId: "", agencyName: "", active: true }]),
          { institution: current.builder?.ownerInstitution || current.institution, agencyId: "", agencyName: "", active: true },
        ],
      },
    }));
  }

  const title = selectedTemplate?.builder?.documentFamilyLabel
    ? `${selectedTemplate.name} · ${selectedTemplate.builder.documentFamilyLabel}`
    : selectedTemplate?.name || "Template Builder";

  if (!canAccess) {
    return (
      <Box p={6} color={textPrimary}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>Only system administrators can use the PCR template builder.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6} bg={pageBg} color={textPrimary} minH="calc(100vh - 32px)" borderRadius="2xl" backdropFilter="blur(18px)">
      <VStack align="stretch" spacing={6}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Box>
            <Heading size="lg">Live QA Template Builder</Heading>
            <Text color={textSecondary} mt={1}>
              Visual-first PCR template creation, field rule workbench, and NEMSIS alignment.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button as={RouterLink} to="/admin/live-qa" variant="outline">
              Live QA Config
            </Button>
            <Button as={RouterLink} to="/admin" variant="ghost">
              Admin Home
            </Button>
          </HStack>
        </Flex>

        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <Flex minH="320px" align="center" justify="center">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <Grid templateColumns={{ base: "1fr", xl: "360px 1fr" }} gap={6}>
            <GridItem>
              <VStack align="stretch" spacing={5}>
                <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                  <Heading size="md" mb={4}>Create Draft Template</Heading>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>Template Name</FormLabel>
                      <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.name} onChange={(event) => setCreateDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Agency PCR template candidate" />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Owner Institution</FormLabel>
                      <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.ownerInstitution} onChange={(event) => setCreateDraft((current) => ({ ...current, ownerInstitution: event.target.value, ownerAgencyId: "" }))}>
                        {config.institutions.map((institution) => (
                          <option key={institution} value={institution}>{institution}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Agency</FormLabel>
                      <HStack align="start">
                        <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.ownerAgencyId} onChange={(event) => setCreateDraft((current) => ({ ...current, ownerAgencyId: event.target.value }))}>
                          <option value="">Institution-level template</option>
                          {filteredAgencies.map((agency) => (
                            <option key={agency.id} value={agency.id}>{agency.name}</option>
                          ))}
                        </Select>
                        <Button onClick={agencyModal.onOpen} flexShrink={0}>New Agency</Button>
                      </HStack>
                    </FormControl>
                    <SimpleGrid columns={2} spacing={3}>
                      <FormControl>
                        <FormLabel>Source Type</FormLabel>
                        <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.sourceType} onChange={(event) => setCreateDraft((current) => ({ ...current, sourceType: event.target.value }))}>
                          {SOURCE_TYPE_OPTIONS.map((entry) => (
                            <option key={entry.value} value={entry.value}>{entry.label}</option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>NEMSIS Version</FormLabel>
                        <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.nemsisVersion} onChange={(event) => setCreateDraft((current) => ({ ...current, nemsisVersion: event.target.value }))}>
                          {config.nemsisReleases.map((release) => (
                            <option key={release.version} value={release.version}>{release.label}</option>
                          ))}
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                    <FormControl isRequired>
                      <FormLabel>Template Candidate Document</FormLabel>
                      <Input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.json,.txt" bg={inputBg} borderColor={borderColor} color={textPrimary} onChange={(event) => setCreateDraft((current) => ({ ...current, file: event.target.files?.[0] || null }))} />
                      <FormHelperText>
                        Every template build runs a visual extraction pass, even when native text exists.
                      </FormHelperText>
                      <HStack justify="space-between" align="start" mt={3} spacing={4}>
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="semibold">This file is already redacted</Text>
                          <Text fontSize="xs" color={textSecondary}>
                            Recommended for template samples. The server will audit it before accepting it.
                          </Text>
                        </Box>
                        <Switch
                          isChecked={createDraft.redactionMode === "pre_redacted"}
                          onChange={(event) => setCreateDraft((current) => ({
                            ...current,
                            redactionMode: event.target.checked ? "pre_redacted" : "auto",
                          }))}
                        />
                      </HStack>
                      {createDraft.redactionMode === "auto" ? (
                        <Text fontSize="xs" color={textSecondary} mt={2}>
                          Unredacted uploads will go through automatic redaction and may be quarantined if the system cannot sanitize them safely.
                        </Text>
                      ) : null}
                      {createDraft.file ? (
                        <Text fontSize="sm" color={textSecondary} mt={2}>
                          Selected: {createDraft.file.name}
                        </Text>
                      ) : null}
                    </FormControl>
                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea bg={inputBg} borderColor={borderColor} color={textPrimary} value={createDraft.description} onChange={(event) => setCreateDraft((current) => ({ ...current, description: event.target.value }))} rows={3} placeholder="Optional context for the template review." />
                    </FormControl>
                    <Button colorScheme="blue" onClick={handleCreateTemplate} isLoading={creating} loadingText="Creating draft">
                      Create and Analyze
                    </Button>
                  </VStack>
                </Box>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                  <Heading size="md" mb={4}>Templates</Heading>
                  <VStack align="stretch" spacing={3} maxH="60vh" overflowY="auto">
                    {templates.map((template) => (
                      <Box
                        key={template.id}
                        borderWidth="1px"
                        borderRadius="lg"
                        p={3}
                        bg={template.id === selectedTemplateId ? "whiteAlpha.160" : "whiteAlpha.080"}
                        borderColor={template.id === selectedTemplateId ? "blue.300" : borderColor}
                        cursor="pointer"
                        onClick={() => void loadTemplate(template.id)}
                      >
                        <HStack justify="space-between" align="start">
                          <Box>
                            <Text fontWeight="semibold">{template.name}</Text>
                            <Text fontSize="sm" color={textSecondary}>{template.institution}</Text>
                          </Box>
                          <Badge colorScheme={statusColor(template.builder?.status || "draft")}>
                            {template.builder?.status || "draft"}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color={textSecondary} mt={2}>
                          {(template.builder?.documentFamilyLabel || "Unclassified")} · {template.fields?.length || 0} fields
                        </Text>
                      </Box>
                    ))}
                    {templates.length === 0 ? <Text color={textSecondary}>No templates yet.</Text> : null}
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            <GridItem>
              {selectedTemplate ? (
                <VStack align="stretch" spacing={5}>
                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                    <Flex justify="space-between" align="start" wrap="wrap" gap={3}>
                      <Box>
                        <Heading size="md">{title}</Heading>
                        <HStack mt={2} spacing={2} wrap="wrap">
                          <Badge colorScheme={statusColor(selectedTemplate.builder?.status || "draft")}>{selectedTemplate.builder?.status || "draft"}</Badge>
                          <Badge>{selectedTemplate.builder?.sourceType || "pcr_sample"}</Badge>
                          <Badge>{selectedTemplate.builder?.nemsisVersion || "3.5.1"}</Badge>
                          {selectedTemplate.builder?.ownerAgency?.name ? <Badge colorScheme="purple">{selectedTemplate.builder.ownerAgency.name}</Badge> : null}
                        </HStack>
                      </Box>
                      <HStack>
                        <Button variant="outline" onClick={handleQueueBuild} isLoading={building}>Rebuild</Button>
                        <Button variant="outline" onClick={handleSaveTemplate} isLoading={saving}>Save Draft</Button>
                        <Button variant="outline" colorScheme="red" onClick={handleDeleteTemplate} isLoading={saving}>Delete</Button>
                        <Button colorScheme="green" onClick={handleActivateTemplate} isLoading={saving}>Activate</Button>
                      </HStack>
                    </Flex>

                    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mt={5}>
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080">
                        <Text fontSize="sm" color={textSecondary}>Document Family</Text>
                        <Text fontWeight="semibold">{selectedTemplate.builder?.documentFamilyLabel || "Pending classification"}</Text>
                      </Box>
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080">
                        <Text fontSize="sm" color={textSecondary}>Owner Institution</Text>
                        <Text fontWeight="semibold">{selectedTemplate.builder?.ownerInstitution || selectedTemplate.institution}</Text>
                      </Box>
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080">
                        <Text fontSize="sm" color={textSecondary}>Fields</Text>
                        <Text fontWeight="semibold">{selectedTemplate.fields?.length || 0}</Text>
                      </Box>
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080">
                        <Text fontSize="sm" color={textSecondary}>Field Discovery</Text>
                        <Text fontWeight="semibold">{extractionBreakdown.total} total fields</Text>
                        <Text fontSize="xs" color={textSecondary} mt={1}>
                          {extractionBreakdown.vision} visual
                          {" · "}
                          {extractionBreakdown.deterministic} fallback
                          {extractionBreakdown.other ? ` · ${extractionBreakdown.other} other` : ""}
                        </Text>
                        {extractionBreakdown.method ? (
                          <Text fontSize="xs" color={textSecondary} mt={1}>
                            Method: {extractionBreakdown.method}
                          </Text>
                        ) : null}
                      </Box>
                    </SimpleGrid>

                    {["queued", "analyzing"].includes(selectedTemplate.builder?.status || "") ? (
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080" mt={4}>
                        <Text fontSize="sm" color={textSecondary}>Build Progress</Text>
                        <Text fontWeight="semibold" mt={1}>
                          {selectedTemplate.builder?.buildProgressCurrent || (selectedTemplate.builder?.status === "queued" ? "Queued for analysis" : "Analyzing")}
                        </Text>
                        {selectedTemplate.builder?.buildProgressPercent != null ? (
                          <Text fontSize="sm" color={textSecondary} mt={1}>
                            {selectedTemplate.builder.buildProgressPercent}% complete
                          </Text>
                        ) : null}
                        {Array.isArray(selectedTemplate.builder?.buildProgressSteps) && selectedTemplate.builder.buildProgressSteps.length > 0 ? (
                          <VStack align="stretch" spacing={2} mt={3}>
                            {selectedTemplate.builder.buildProgressSteps.map((step, index) => (
                              <HStack key={`${step.label}-${index}`} justify="space-between">
                                <Text fontSize="sm">{step.label}</Text>
                                <Badge colorScheme={
                                  step.state === "complete"
                                    ? "green"
                                    : step.state === "active"
                                      ? "blue"
                                      : step.state === "failed"
                                        ? "red"
                                        : "gray"
                                }>
                                  {step.state}
                                </Badge>
                              </HStack>
                            ))}
                          </VStack>
                        ) : null}
                      </Box>
                    ) : null}

                    {selectedTemplate.builder?.buildError ? (
                      <Alert status="error" borderRadius="lg" mt={4}>
                        <AlertIcon />
                        <AlertDescription>{selectedTemplate.builder.buildError}</AlertDescription>
                      </Alert>
                    ) : null}

                    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} bg="whiteAlpha.080" mt={4}>
                      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} gap={3} direction={{ base: "column", md: "row" }}>
                        <Box>
                          <Text fontSize="sm" color={textSecondary}>Source document</Text>
                          {selectedTemplateSourceDocument?.url ? (
                            <VStack align="start" spacing={1} mt={1}>
                              <ChakraLink href={selectedTemplateSourceDocument.url} isExternal color="blue.200" fontWeight="semibold">
                                {selectedTemplateSourceDocument.fileName || "Attached source document"}
                              </ChakraLink>
                              <Text fontSize="sm" color={textSecondary}>
                                {selectedTemplateSourceDocument.mimeType || "application/pdf"}
                              </Text>
                              {selectedTemplateSourceDocument.artifactSource ? (
                                <Text fontSize="xs" color={textSecondary}>
                                  Source: {selectedTemplateSourceDocument.artifactSource === "uploaded_pre_redacted" ? "uploaded redacted file" : "server-redacted file"}
                                </Text>
                              ) : null}
                            </VStack>
                          ) : (
                            <Text fontWeight="semibold" mt={1}>No source document is attached to this template</Text>
                          )}
                        </Box>
                        <Box minW={{ md: "320px" }}>
                          <FormControl>
                            <FormLabel mb={2}>Attach or replace sample</FormLabel>
                            <Input
                              ref={selectedTemplateFileInputRef}
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg,.json,.txt"
                              bg={inputBg}
                              borderColor={borderColor}
                              color={textPrimary}
                              onChange={(event) => setSelectedTemplateFile(event.target.files?.[0] || null)}
                            />
                            <FormHelperText>
                              Save Draft updates the attached sample. Rebuild will use the newly selected file immediately.
                            </FormHelperText>
                            <HStack justify="space-between" align="start" mt={3} spacing={4}>
                              <Box flex="1">
                                <Text fontSize="sm" fontWeight="semibold">This file is already redacted</Text>
                                <Text fontSize="xs" color={textSecondary}>
                                  Recommended when replacing the sample with a manually sanitized chart.
                                </Text>
                              </Box>
                              <Switch
                                isChecked={selectedTemplateRedactionMode === "pre_redacted"}
                                onChange={(event) => setSelectedTemplateRedactionMode(event.target.checked ? "pre_redacted" : "auto")}
                              />
                            </HStack>
                            {selectedTemplateRedactionMode === "auto" ? (
                              <Text fontSize="xs" color={textSecondary} mt={2}>
                                Unredacted uploads will be auto-redacted and may be rejected if the output does not pass the PHI audit.
                              </Text>
                            ) : null}
                            {pendingTemplateFile ? (
                              <Text fontSize="sm" color={textSecondary} mt={2}>
                                Pending upload: {pendingTemplateFile.name}
                              </Text>
                            ) : null}
                          </FormControl>
                        </Box>
                      </Flex>
                    </Box>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                    <Flex justify="space-between" align="center" mb={4}>
                      <Heading size="md">Assignments</Heading>
                      <Button size="sm" onClick={addAssignment}>Add Assignment</Button>
                    </Flex>
                    <VStack align="stretch" spacing={3}>
                      {(selectedTemplate.builder?.assignments || []).map((assignment, index) => (
                        <SimpleGrid key={`${assignment.institution}-${index}`} columns={{ base: 1, md: 4 }} spacing={3}>
                          <FormControl>
                            <FormLabel>Institution</FormLabel>
                            <Select
                              bg={inputBg}
                              borderColor={borderColor}
                              color={textPrimary}
                              value={assignment.institution || ""}
                              onChange={(event) => patchSelectedTemplate((current) => {
                                const next = clone(current);
                                next.builder.assignments[index].institution = event.target.value;
                                return next;
                              })}
                            >
                              {config.institutions.map((institution) => (
                                <option key={institution} value={institution}>{institution}</option>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl>
                            <FormLabel>Agency</FormLabel>
                            <Select
                              bg={inputBg}
                              borderColor={borderColor}
                              color={textPrimary}
                              value={assignment.agencyId || ""}
                              onChange={(event) => patchSelectedTemplate((current) => {
                                const next = clone(current);
                                const agency = config.agencies.find((entry) => entry.id === event.target.value);
                                next.builder.assignments[index].agencyId = event.target.value;
                                next.builder.assignments[index].agencyName = agency?.name || "";
                                return next;
                              })}
                            >
                              <option value="">Institution fallback</option>
                              {config.agencies.map((agency) => (
                                <option key={agency.id} value={agency.id}>{agency.name}</option>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl display="flex" alignItems="center" pt={8}>
                            <FormLabel mb="0">Active</FormLabel>
                            <Switch
                              isChecked={assignment.active !== false}
                              onChange={(event) => patchSelectedTemplate((current) => {
                                const next = clone(current);
                                next.builder.assignments[index].active = event.target.checked;
                                return next;
                              })}
                            />
                          </FormControl>
                        </SimpleGrid>
                      ))}
                      {(selectedTemplate.builder?.assignments || []).length === 0 ? (
                        <Text color={textSecondary}>No assignments yet. Add institution or agency targets before activation.</Text>
                      ) : null}
                    </VStack>
                  </Box>

                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                    <Flex justify="space-between" align="center" mb={4}>
                      <Heading size="md">Field Workbench</Heading>
                      <HStack spacing={3}>
                        {protectedFieldIds.size ? (
                          <Badge colorScheme="gray" variant="subtle">
                            {protectedFieldIds.size} protected
                          </Badge>
                        ) : null}
                        <Button size="sm" onClick={addManualField}>Add Field</Button>
                      </HStack>
                    </Flex>
                    <VStack align="stretch" spacing={6}>
                      <Accordion allowMultiple defaultIndex={defaultExpandedSections}>
                        {groupedFields.map((section) => (
                          <AccordionItem
                            key={section.key}
                            borderWidth="1px"
                            borderColor={section.isLocked ? "whiteAlpha.120" : borderColor}
                            borderRadius="xl"
                            bg={section.isLocked ? "whiteAlpha.040" : "transparent"}
                            opacity={section.isLocked ? 0.72 : 1}
                            mb={4}
                          >
                            <AccordionButton px={4} py={3}>
                              <Flex flex="1" justify="space-between" align="center" gap={3}>
                                <HStack spacing={3}>
                                  <Heading size="sm">{section.label || "Fields"}</Heading>
                                  <Badge colorScheme="gray" variant="subtle">{section.fields.length}</Badge>
                                  {section.isLocked ? <Badge colorScheme="gray">Locked</Badge> : null}
                                  {!section.isLocked && section.hasSensitiveFields ? <Badge colorScheme="yellow">Contains PHI fields</Badge> : null}
                                </HStack>
                                <AccordionIcon />
                              </Flex>
                            </AccordionButton>
                            <AccordionPanel pt={1} pb={4}>
                              {section.isLocked ? (
                                <Alert status="warning" borderRadius="lg" mb={4} bg="whiteAlpha.080">
                                  <AlertIcon />
                                  <AlertDescription fontSize="sm">
                                    This section was identified as PHI-bearing in the template sample and is locked to prevent storing live values.
                                  </AlertDescription>
                                </Alert>
                              ) : section.hasSensitiveFields ? (
                                <Alert status="info" borderRadius="lg" mb={4} bg="whiteAlpha.080">
                                  <AlertIcon />
                                  <AlertDescription fontSize="sm">
                                    PHI-bearing fields in this section are locked. Non-sensitive fields remain editable.
                                  </AlertDescription>
                                </Alert>
                              ) : null}

                              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                                {section.fields.map((field) => {
                                  const isLockedField = protectedFieldIds.has(String(field.id || ""));
                                  const isNotRealField = field?.notReal === true;
                                  const redactionHint = redactionHintsByFieldId.get(String(field.id || ""));
                                  const sourceKind = String(field.evidence?.source || "").trim().toLowerCase();
                                  return (
                                    <Box
                                      key={field.id}
                                      borderWidth="1px"
                                      borderColor={isNotRealField ? "orange.300" : isLockedField ? "whiteAlpha.140" : "whiteAlpha.220"}
                                      borderRadius="xl"
                                      p={4}
                                      bg={isNotRealField ? "orange.50" : isLockedField ? "whiteAlpha.030" : "whiteAlpha.050"}
                                      opacity={isNotRealField || isLockedField ? 0.68 : 1}
                                      boxShadow="inset 0 1px 0 rgba(255,255,255,0.04)"
                                    >
                                      <VStack align="stretch" spacing={3}>
                                        <Flex justify="space-between" align="start" gap={3}>
                                          <Box minW={0}>
                                            <Heading size="sm" color={textPrimary} noOfLines={2}>
                                              {field.plainEnglishLabel || field.sourceLabel || "Untitled field"}
                                            </Heading>
                                            {field.sourceLabel && field.sourceLabel !== field.plainEnglishLabel ? (
                                              <Text color={textSecondary} fontSize="xs" mt={1} noOfLines={1}>
                                                {field.sourceLabel}
                                              </Text>
                                            ) : null}
                                          </Box>
                                          <HStack spacing={2} wrap="wrap" justify="end">
                                            {isNotRealField ? <Badge colorScheme="orange">Not real</Badge> : null}
                                            {field.templateRedacted ? <Badge colorScheme="red" variant="solid">Redacted in template</Badge> : null}
                                            {isLockedField ? <Badge colorScheme="red">Protected</Badge> : null}
                                            <Badge colorScheme="purple" variant="subtle">
                                              {String(field.reviewDomain || "clinical")}
                                            </Badge>
                                            {redactionHint ? <Badge colorScheme="red" variant="subtle">{phiHintLabel(redactionHint.hintType)}</Badge> : null}
                                            {field.mandatory ? <Badge colorScheme="orange">Required</Badge> : null}
                                            {field.hasCustomRule ? <Badge colorScheme="blue">Rule</Badge> : null}
                                            {field.hasNemsisAlignment ? <Badge colorScheme="green">NEMSIS</Badge> : null}
                                            {!isFallbackOnlyExtraction && sourceKind && sourceKind !== "deterministic" ? (
                                              <Badge colorScheme="gray" variant="subtle">{sourceKind}</Badge>
                                            ) : null}
                                          </HStack>
                                        </Flex>

                                        {field.locationDescription && !isFallbackOnlyExtraction ? (
                                          <Text fontSize="xs" color={textSecondary} noOfLines={2}>
                                            {field.locationDescription}
                                          </Text>
                                        ) : null}

                                        {isNotRealField ? (
                                          <Alert status="warning" borderRadius="md" bg="orange.50">
                                            <AlertIcon />
                                            <AlertDescription fontSize="xs">
                                              This field is marked as not real. It will stay on the template for audit, but it is excluded from compiled template matching, live ingestion, and evaluation.
                                            </AlertDescription>
                                          </Alert>
                                        ) : isLockedField ? (
                                          <Alert status="warning" borderRadius="md" bg="whiteAlpha.070">
                                            <AlertIcon />
                                            <AlertDescription fontSize="xs">
                                              This field is protected at the template level and live values should not be retained or sent downstream.
                                            </AlertDescription>
                                          </Alert>
                                        ) : redactionHint ? (
                                          <Alert status="info" borderRadius="md" bg="whiteAlpha.070">
                                            <AlertIcon />
                                            <AlertDescription fontSize="xs">
                                              Sensitive field type detected. Leave protected unless you have a specific retention reason.
                                            </AlertDescription>
                                          </Alert>
                                        ) : null}

                                        <HStack justify="space-between" align="center">
                                          <Text fontSize="xs" color={textSecondary}>
                                            Template validity
                                          </Text>
                                          <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme={isNotRealField ? "green" : "orange"}
                                            onClick={() => updateField(field.id, (entry) => ({
                                              ...entry,
                                              notReal: !entry.notReal,
                                            }))}
                                          >
                                            {isNotRealField ? "Restore field" : "Mark not real"}
                                          </Button>
                                        </HStack>

                                        {canOverrideProtectedFields ? (
                                          <HStack justify="space-between" align="center">
                                            <Text fontSize="xs" color={textSecondary}>
                                              System-admin override
                                            </Text>
                                            <Button
                                              size="xs"
                                              variant="outline"
                                              colorScheme={isLockedField ? "orange" : "red"}
                                              onClick={() => updateField(field.id, (entry) => ({
                                                ...entry,
                                                isProtected: !entry.isProtected,
                                                protectionOverride: !entry.isProtected ? "protected" : "unprotected",
                                                protectionReason: !entry.isProtected
                                                  ? (redactionHint ? `System-admin protected: ${redactionHint.hintType}` : "System-admin protected")
                                                  : "",
                                                protectionRequestStatus: "",
                                              }))}
                                            >
                                              {isLockedField ? "Allow retention" : "Protect field"}
                                            </Button>
                                          </HStack>
                                        ) : canManageProtectionRequests ? (
                                          <HStack justify="space-between" align="center">
                                            <Text fontSize="xs" color={textSecondary}>
                                              Institution admin action
                                            </Text>
                                            {isLockedField ? (
                                              <Button
                                                size="xs"
                                                variant="outline"
                                                colorScheme="orange"
                                                onClick={() => updateField(field.id, (entry) => ({
                                                  ...entry,
                                                  protectionRequestStatus: entry.protectionRequestStatus === "requested_unprotect" ? "" : "requested_unprotect",
                                                }))}
                                              >
                                                {field.protectionRequestStatus === "requested_unprotect" ? "Withdraw request" : "Request retention"}
                                              </Button>
                                            ) : (
                                              <Button
                                                size="xs"
                                                variant="outline"
                                                colorScheme="red"
                                                onClick={() => updateField(field.id, (entry) => ({
                                                  ...entry,
                                                  isProtected: true,
                                                  protectionOverride: "protected",
                                                  protectionReason: entry.protectionReason || "Institution admin protected",
                                                  protectionRequestStatus: "",
                                                }))}
                                              >
                                                Mark as PHI
                                              </Button>
                                            )}
                                          </HStack>
                                        ) : null}

                                        {field.protectionRequestStatus === "requested_unprotect" ? (
                                          <Alert status="info" borderRadius="md" bg="whiteAlpha.070">
                                            <AlertIcon />
                                            <AlertDescription fontSize="xs">
                                              Retention override requested. A system-admin must approve before this field can capture values.
                                            </AlertDescription>
                                          </Alert>
                                        ) : null}

                                        <FormControl isDisabled={isLockedField || isNotRealField}>
                                          <Input
                                            bg={inputBg}
                                            borderColor={borderColor}
                                            color={textPrimary}
                                            value={field.plainEnglishLabel || ""}
                                            onChange={(event) => updateField(field.id, (entry) => ({ ...entry, plainEnglishLabel: event.target.value }))}
                                            placeholder="Field name"
                                          />
                                        </FormControl>

                                        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2}>
                                          <FormControl isDisabled={isLockedField || isNotRealField}>
                                            <Select
                                              bg={inputBg}
                                              borderColor={borderColor}
                                              color={textPrimary}
                                              value={field.reviewDomain || "clinical"}
                                              onChange={(event) => updateField(field.id, (entry) => ({ ...entry, reviewDomain: event.target.value }))}
                                            >
                                              {REVIEW_DOMAIN_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <FormControl isDisabled={isLockedField || isNotRealField}>
                                            <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.mandatory ? "yes" : "no"} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, mandatory: event.target.value === "yes" }))}>
                                              <option value="no">Optional</option>
                                              <option value="yes">Required</option>
                                            </Select>
                                          </FormControl>
                                          <FormControl isDisabled={isLockedField || isNotRealField || !field.mandatory}>
                                            <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.missingSeverity || "warn"} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, missingSeverity: event.target.value }))}>
                                              <option value="warn">Warn</option>
                                              <option value="fail">Fail</option>
                                            </Select>
                                          </FormControl>
                                          <FormControl isDisabled={isLockedField || isNotRealField}>
                                            <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.hasCustomRule ? "yes" : "no"} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, hasCustomRule: event.target.value === "yes" }))}>
                                              <option value="no">No rule</option>
                                              <option value="yes">Rule</option>
                                            </Select>
                                          </FormControl>
                                          <FormControl isDisabled={isLockedField || isNotRealField}>
                                            <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.hasNemsisAlignment ? "yes" : "no"} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, hasNemsisAlignment: event.target.value === "yes" }))}>
                                              <option value="no">No NEMSIS</option>
                                              <option value="yes">NEMSIS</option>
                                            </Select>
                                          </FormControl>
                                        </SimpleGrid>

                                        {!isLockedField && !isNotRealField && (field.aiSuggestions?.mandatoryCandidate || field.aiSuggestions?.customRuleCandidate || field.aiSuggestions?.plainEnglishLabel) ? (
                                          <Text fontSize="xs" color={textSecondary}>
                                            {field.aiSuggestions?.plainEnglishLabel && field.aiSuggestions.plainEnglishLabel !== field.plainEnglishLabel
                                              ? `AI label: ${field.aiSuggestions.plainEnglishLabel}`
                                              : field.aiSuggestions?.mandatoryCandidate
                                                ? "AI suggests this may be required."
                                                : field.aiSuggestions?.customRuleHint || "AI sees a likely local rule."}
                                          </Text>
                                        ) : null}

                                        {field.hasCustomRule && !isLockedField && !isNotRealField ? (
                                          <Box pt={2} borderTopWidth="1px" borderTopColor="whiteAlpha.160">
                                            <Stack spacing={3}>
                                              <Textarea bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.customRuleText || ""} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, customRuleText: event.target.value }))} rows={3} placeholder="Rule text" />
                                              <HStack align="start" spacing={3}>
                                                <Textarea value={field.generatedInstructionText || ""} readOnly rows={3} bg="whiteAlpha.100" borderColor={borderColor} color={textPrimary} placeholder="Generated instruction" />
                                                <Button alignSelf="stretch" onClick={() => void handleGenerateInstruction(field)} isDisabled={!field.customRuleText} isLoading={field.instructionStatus === "running"}>
                                                  Generate
                                                </Button>
                                              </HStack>
                                            </Stack>
                                          </Box>
                                        ) : null}

                                        {field.hasNemsisAlignment && !isLockedField && !isNotRealField ? (
                                          <Box pt={2} borderTopWidth="1px" borderTopColor="whiteAlpha.160">
                                            <Stack spacing={3}>
                                              <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={nemsisSearch[field.id] || ""} onChange={(event) => void handleNemsisSearch(field.id, event.target.value)} placeholder="Search NEMSIS" />
                                              <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={field.nemsisElementId || ""} onChange={(event) => updateField(field.id, (entry) => ({ ...entry, nemsisElementId: event.target.value }))} placeholder="Selected element ID" />
                                              <Stack spacing={2}>
                                                {[...(field.nemsisCandidates || []), ...(nemsisResults[field.id] || [])]
                                                  .filter((candidate, index, array) => array.findIndex((entry) => entry.elementId === candidate.elementId) === index)
                                                  .slice(0, 6)
                                                  .map((candidate) => (
                                                    <Button
                                                      key={candidate.elementId}
                                                      justifyContent="space-between"
                                                      variant={field.nemsisElementId === candidate.elementId ? "solid" : "outline"}
                                                      onClick={() => updateField(field.id, (entry) => ({
                                                        ...entry,
                                                        nemsisElementId: candidate.elementId,
                                                        hasNemsisAlignment: true,
                                                      }))}
                                                    >
                                                      <Text>{candidate.elementId}</Text>
                                                      <Text fontSize="xs" color="gray.300">{candidate.title}</Text>
                                                    </Button>
                                                  ))}
                                              </Stack>
                                            </Stack>
                                          </Box>
                                        ) : null}
                                      </VStack>
                                    </Box>
                                  );
                                })}
                              </SimpleGrid>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                      {(selectedTemplate.fields || []).length === 0 ? (
                        <Text color={textSecondary}>
                          No fields yet. Run the build or add fields manually.
                        </Text>
                      ) : null}
                    </VStack>
                  </Box>
                </VStack>
              ) : (
                <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={8} bg={cardBg} color={textPrimary} boxShadow="xl" backdropFilter="blur(12px)">
                  <Text color={textSecondary}>Create a template or pick an existing draft to start editing.</Text>
                </Box>
              )}
            </GridItem>
          </Grid>
        )}
      </VStack>

      <Modal isOpen={agencyModal.isOpen} onClose={agencyModal.onClose}>
        <ModalOverlay />
        <ModalContent bg="#111827" color={textPrimary} borderWidth="1px" borderColor={borderColor}>
          <ModalHeader>Create Agency</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={newAgency.name} onChange={(event) => setNewAgency((current) => ({ ...current, name: event.target.value }))} />
              </FormControl>
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>City</FormLabel>
                  <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={newAgency.city} onChange={(event) => setNewAgency((current) => ({ ...current, city: event.target.value }))} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>State</FormLabel>
                  <Input bg={inputBg} borderColor={borderColor} color={textPrimary} value={newAgency.state} onChange={(event) => setNewAgency((current) => ({ ...current, state: event.target.value }))} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel>Facility Type</FormLabel>
                <Select bg={inputBg} borderColor={borderColor} color={textPrimary} value={newAgency.facilityType} onChange={(event) => setNewAgency((current) => ({ ...current, facilityType: event.target.value }))}>
                  {FACILITY_TYPES.map((facilityType) => (
                    <option key={facilityType} value={facilityType}>{facilityType}</option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={agencyModal.onClose}>Cancel</Button>
            <Button
              colorScheme="blue"
              onClick={async () => {
                try {
                  const response = await api.fetch("/live-qa/template-agency", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      institution: selectedTemplate?.builder?.ownerInstitution || createDraft.ownerInstitution,
                      ...newAgency,
                    }),
                  });
                  const json = await parseJsonResponse(response, "Create agency request did not return JSON");
                  if (!json.success) throw new Error(getRouteErrorMessage(json, "Failed to create agency"));
                  if (json.agency) {
                    setConfig((current) => ({
                      ...current,
                      agencies: [...current.agencies, json.agency],
                    }));
                    if (selectedTemplate) {
                      patchSelectedTemplate((current) => ({
                        ...current,
                        builder: {
                          ...(current.builder || {}),
                          ownerAgency: {
                            id: json.agency.id,
                            name: json.agency.name,
                            city: json.agency.city,
                            state: json.agency.state,
                            facilityType: json.agency.facilityType,
                          },
                        },
                      }));
                    } else {
                      setCreateDraft((current) => ({ ...current, ownerAgencyId: json.agency.id }));
                    }
                  }
                  setNewAgency(defaultAgencyDraft());
                  agencyModal.onClose();
                  toast({ status: "success", title: "Agency created" });
                } catch (agencyError) {
                  setError(agencyError.message || "Failed to create agency");
                }
              }}
            >
              Create Agency
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
